import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "@/app/store";
import { clearOwnCache, clearUserCache } from "@/features/users/model/usersSlice";

export function useClearUserCache() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("users");
  const [isClearing, setIsClearing] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);

  const clearClearError = useCallback(() => {
    setClearError(null);
  }, []);

  /** Clear the logged-in user's own cache, or `userId`'s when given. */
  const clearCache = useCallback(
    async (userId?: number) => {
      setClearError(null);
      setIsClearing(true);

      try {
        // Dispatched in separate branches rather than through one ternary: the two thunks have
        // different argument types, and their union does not satisfy `dispatch`.
        if (userId === undefined) {
          await dispatch(clearOwnCache()).unwrap();
        } else {
          await dispatch(clearUserCache(userId)).unwrap();
        }
      } catch (error) {
        const message =
          typeof error === "string" ? error : t("cache.errors.clearFailed");
        setClearError(message);
        throw error;
      } finally {
        setIsClearing(false);
      }
    },
    [dispatch, t],
  );

  return { clearCache, isClearing, clearError, clearClearError };
}
